<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { format, fromUnixTime } from 'date-fns';

import ReportsAPI from 'dashboard/api/reports';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilterSelector from './components/FilterSelector.vue';
import GaugeChart from './components/charts/GaugeChart.vue';
import LineChart from './components/charts/LineChart.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import SegmentedProgressBar from './components/charts/SegmentedProgressBar.vue';
import ClindCsatTable from './components/tables/ClindCsatTable.vue';

const store = useStore();
const { t } = useI18n();

// Filter state
const from = ref(0);
const to = ref(0);
const inboxIds = ref([]);
const businessHours = ref(false);
const pageIndex = ref(0);

const csatRequestPayload = computed(() => ({
  from: from.value,
  to: to.value,
  inbox_ids: inboxIds.value,
  assigned_only: false,
}));

// Loading state
const isLoading = ref(false);

// Metrics data
const retentionRate = ref(0);
const csatBot = ref(0);
const botConversations = ref(0);
const totalConversations = ref(0);
const csatBreakdown = ref({
  promoters: 0,
  neutral: 0,
  detractors: 0,
  total: 0,
});

const botPercentage = computed(() => {
  if (totalConversations.value === 0) return 0;
  return Math.round((botConversations.value / totalConversations.value) * 100);
});

const segmentedBarData = computed(() => {
  const { promoters, neutral, detractors, total } = csatBreakdown.value;
  if (total === 0) {
    return [
      {
        label: t('BOT_REPORTS.SEGMENTS.PROMOTERS'),
        value: 0,
        percentage: 0,
        color: '#16a34a',
      },
      {
        label: t('BOT_REPORTS.SEGMENTS.NEUTRAL'),
        value: 0,
        percentage: 0,
        color: '#eab308',
      },
      {
        label: t('BOT_REPORTS.SEGMENTS.DETRACTORS'),
        value: 0,
        percentage: 0,
        color: '#dc2626',
      },
    ];
  }

  return [
    {
      label: t('BOT_REPORTS.SEGMENTS.PROMOTERS'),
      value: promoters,
      percentage: Math.round((promoters / total) * 100),
      color: '#16a34a',
    },
    {
      label: t('BOT_REPORTS.SEGMENTS.NEUTRAL'),
      value: neutral,
      percentage: Math.round((neutral / total) * 100),
      color: '#eab308',
    },
    {
      label: t('BOT_REPORTS.SEGMENTS.DETRACTORS'),
      value: detractors,
      percentage: Math.round((detractors / total) * 100),
      color: '#dc2626',
    },
  ];
});

// Chart data
const retentionChartData = ref({ labels: [], datasets: [] });
const botVsHumanChartData = ref({ labels: [], datasets: [] });
const csatBotChartData = ref({ labels: [], datasets: [] });

const retentionChartOptions = {
  scales: {
    y: {
      min: 0,
      max: 100,
      ticks: {
        callback: value => `${value}%`,
      },
    },
  },
  plugins: {
    tooltip: {
      callbacks: {
        label: context => `${context.parsed.y.toFixed(1)}%`,
      },
    },
  },
};

const botVsHumanChartOptions = {
  plugins: {
    legend: {
      display: true,
      position: 'top',
    },
  },
  scales: {
    x: {
      stacked: true,
    },
    y: {
      stacked: true,
      beginAtZero: true,
    },
  },
};

const csatBotChartOptions = {
  scales: {
    y: {
      min: 0,
      max: 100,
      ticks: {
        callback: value => `${value}%`,
      },
    },
  },
  plugins: {
    tooltip: {
      callbacks: {
        label: context => `${context.parsed.y.toFixed(1)}%`,
      },
    },
  },
};

const formatTimestampLabel = timestamp => {
  const rangeInDays = (to.value - from.value) / 86400;

  if (rangeInDays <= 1) {
    return format(fromUnixTime(timestamp), 'HH:mm');
  }
  return format(fromUnixTime(timestamp), 'd MMM');
};

const updateSummaryMetrics = data => {
  retentionRate.value = data.retention_rate || 0;
  csatBot.value = data.bot_csat || 0;
  botConversations.value = data.bot_conversations || 0;
  totalConversations.value = data.total_conversations || 0;
  csatBreakdown.value = data.csat_breakdown || {
    promoters: 0,
    neutral: 0,
    detractors: 0,
    total: 0,
  };
};

const updateChartData = data => {
  const retentionData = data.retention || [];
  const botCsatData = data.bot_csat || [];
  const botConvData = data.bot_conversations || [];
  const humanConvData = data.human_conversations || [];

  const labels = retentionData.map(item =>
    formatTimestampLabel(item.timestamp)
  );

  retentionChartData.value = {
    labels,
    datasets: [
      {
        label: t('BOT_REPORTS.METRIC.RETENTION_RATE.LABEL'),
        data: retentionData.map(item => item.value),
        borderColor: '#16a34a',
        backgroundColor: 'rgba(22, 163, 74, 0.1)',
        fill: true,
      },
    ],
  };

  botVsHumanChartData.value = {
    labels,
    datasets: [
      {
        label: t('BOT_REPORTS.CHART.BOT_VS_HUMAN.BOT'),
        data: botConvData.map(item => item.value),
        backgroundColor: '#3b82f6',
      },
      {
        label: t('BOT_REPORTS.CHART.BOT_VS_HUMAN.HUMAN'),
        data: humanConvData.map(item => item.value),
        backgroundColor: '#94a3b8',
      },
    ],
  };

  csatBotChartData.value = {
    labels,
    datasets: [
      {
        label: t('BOT_REPORTS.METRIC.CSAT_BOT.LABEL'),
        data: botCsatData.map(item => item.value),
        borderColor: '#eab308',
        backgroundColor: 'rgba(234, 179, 8, 0.1)',
        fill: true,
      },
    ],
  };
};

const fetchBotReports = async () => {
  if (!from.value || !to.value) return;

  isLoading.value = true;
  try {
    const [summaryRes, timeseriesRes] = await Promise.all([
      ReportsAPI.getBotSummary({
        from: from.value,
        to: to.value,
        inboxIds: inboxIds.value,
        businessHours: businessHours.value,
      }),
      ReportsAPI.getBotTimeseries({
        from: from.value,
        to: to.value,
        inboxIds: inboxIds.value,
        businessHours: businessHours.value,
      }),
    ]);

    updateSummaryMetrics(summaryRes.data);
    updateChartData(timeseriesRes.data);

    pageIndex.value = 0;
    store.dispatch('csat/getMetrics', csatRequestPayload.value);
    store.dispatch('csat/get', { page: 1, ...csatRequestPayload.value });
  } finally {
    isLoading.value = false;
  }
};

// Filter change handler
const onFilterChange = ({
  from: fromDate,
  to: toDate,
  selectedInboxes,
  businessHours: bh,
}) => {
  from.value = fromDate;
  to.value = toDate;
  inboxIds.value = selectedInboxes?.map(el => el.id) || [];
  businessHours.value = bh;
};

const onCsatPageChange = newPageIndex => {
  pageIndex.value = newPageIndex;
  store.dispatch('csat/get', {
    page: newPageIndex + 1,
    ...csatRequestPayload.value,
  });
};

// Watch for filter changes and fetch data
watch([from, to, inboxIds, businessHours], () => {
  fetchBotReports();
});
</script>

<template>
  <ReportHeader :header-title="$t('BOT_REPORTS.HEADER')" />

  <div class="flex flex-col gap-4">
    <!-- Filters -->
    <ReportFilterSelector
      show-inbox-filter
      show-business-hours-switch
      @filter-change="onFilterChange"
    />

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <span class="text-n-slate-11">{{ $t('REPORT.LOADING_CHART') }}</span>
    </div>

    <template v-else>
      <!-- Metric Cards -->
      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <!-- Retention Rate Card with Gauge -->
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
          <div class="flex flex-col items-center">
            <h3
              class="flex items-center m-0 text-sm font-medium text-n-slate-11"
            >
              <span>{{ $t('BOT_REPORTS.METRIC.RETENTION_RATE.LABEL') }}</span>
              <fluent-icon
                v-tooltip="$t('BOT_REPORTS.METRIC.RETENTION_RATE.TOOLTIP')"
                size="14"
                icon="info"
                class="text-n-slate-11 my-0 mx-1 mt-0.5"
              />
            </h3>
            <GaugeChart
              :value="retentionRate"
              size="md"
              :thresholds="{ good: 75, warning: 70 }"
              class="mt-2"
            />
          </div>
        </div>

        <!-- CSAT Bot Card with Gauge -->
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
          <div class="flex flex-col items-center">
            <h3
              class="flex items-center m-0 text-sm font-medium text-n-slate-11"
            >
              <span>{{ $t('BOT_REPORTS.METRIC.CSAT_BOT.LABEL') }}</span>
              <fluent-icon
                v-tooltip="$t('BOT_REPORTS.METRIC.CSAT_BOT.TOOLTIP')"
                size="14"
                icon="info"
                class="text-n-slate-11 my-0 mx-1 mt-0.5"
              />
            </h3>
            <GaugeChart
              :value="csatBot"
              size="md"
              :thresholds="{ good: 80, warning: 70 }"
              class="mt-2"
            />
          </div>
        </div>

        <!-- Bot Conversations Card (no gauge, just number) -->
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
          <div class="flex flex-col items-center">
            <h3
              class="flex items-center m-0 text-sm font-medium text-n-slate-11"
            >
              <span>{{
                $t('BOT_REPORTS.METRIC.BOT_CONVERSATIONS.LABEL')
              }}</span>
              <fluent-icon
                v-tooltip="$t('BOT_REPORTS.METRIC.BOT_CONVERSATIONS.TOOLTIP')"
                size="14"
                icon="info"
                class="text-n-slate-11 my-0 mx-1 mt-0.5"
              />
            </h3>
            <div
              class="flex flex-col items-center justify-center mt-2 h-[110px]"
            >
              <span class="text-3xl font-semibold text-n-slate-12">
                {{ botConversations.toLocaleString() }}
              </span>
              <span class="mt-1 text-sm text-n-slate-11">
                ({{ botPercentage }}%
                {{ $t('BOT_REPORTS.METRIC.BOT_CONVERSATIONS.TOTAL') }})
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Promoters / Neutral / Detractors -->
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
        <h3 class="mb-4 text-base font-medium text-n-slate-12">
          {{ $t('BOT_REPORTS.SEGMENTS.TITLE') }}
        </h3>
        <SegmentedProgressBar
          :segments="segmentedBarData"
          height="32px"
          preserve-order
        />
      </div>

      <!-- CSAT Table -->
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
        <h3 class="mb-4 text-base font-medium text-n-slate-12">
          {{ $t('BOT_REPORTS.CSAT_TABLE.TITLE') }}
        </h3>
        <ClindCsatTable
          :page-index="pageIndex"
          @page-change="onCsatPageChange"
        />
      </div>

      <!-- Chart 1: Retention Over Time -->
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
        <h3 class="mb-4 text-base font-medium text-n-slate-12">
          {{ $t('BOT_REPORTS.CHART.RETENTION_OVER_TIME.TITLE') }}
        </h3>
        <div class="h-64">
          <LineChart
            :collection="retentionChartData"
            :chart-options="retentionChartOptions"
          />
        </div>
      </div>

      <!-- Chart 2: Bot vs Human -->
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
        <h3 class="mb-4 text-base font-medium text-n-slate-12">
          {{ $t('BOT_REPORTS.CHART.BOT_VS_HUMAN.TITLE') }}
        </h3>
        <div class="h-64">
          <BarChart
            :collection="botVsHumanChartData"
            :chart-options="botVsHumanChartOptions"
          />
        </div>
      </div>

      <!-- Chart 3: CSAT Bot Evolution -->
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
        <h3 class="mb-4 text-base font-medium text-n-slate-12">
          {{ $t('BOT_REPORTS.CHART.CSAT_EVOLUTION.TITLE') }}
        </h3>
        <div class="h-64">
          <LineChart
            :collection="csatBotChartData"
            :chart-options="csatBotChartOptions"
          />
        </div>
      </div>
    </template>
  </div>
</template>
